class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}

customElements.define('facet-short', FacetShort, { extends: 'select' });
class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 95,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);
            this.renderSearchTypeTabs(responseText);
            this.logQuery(url, responseText, 'facet-update');

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
            this.cachedMap.set(url, responseText);

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);
      this.renderSearchTypeTabs(responseText);
      this.logQuery(url, responseText, 'facet-cache');

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
    );

    // Log filter information for debugging
    const url = new URL(window.location.href);
    const searchType = url.searchParams.get('type') || 'all';
    const searchQuery = url.searchParams.get('q') || '';

    console.group(`[Filter Facets] Search: "${searchQuery}" | Type: ${searchType}`);

    facetElements.forEach((element, index) => {
      const filterIndex = element.dataset.index;
      const filterLabel = element.querySelector('summary .h4, .h4')?.textContent?.trim() || 'Unknown';
      const filterType = element.querySelector('input[type="checkbox"]') ? 'checkbox' :
                        element.querySelector('input[type="range"]') ? 'price_range' : 'unknown';
      const valueCount = element.querySelectorAll('input[type="checkbox"], input[type="radio"]').length;


    });
    console.groupEnd();

    // If no facets in response, hide the facet containers completely
    if (facetElements.length === 0) {
      const currentFacetContainer = document.getElementById('FacetFiltersContainer');
      const facetDrawerWrapper = document.querySelector('.facet-drawer-wrapper');

      if (currentFacetContainer) {
        currentFacetContainer.style.display = 'none';
      }
      if (facetDrawerWrapper) {
        facetDrawerWrapper.style.display = 'none';
      }
      return; // Exit early - nothing to render
    }

    // Show facet containers if they were hidden
    const currentFacetContainer = document.getElementById('FacetFiltersContainer');
    const facetDrawerWrapper = document.querySelector('.facet-drawer-wrapper');
    if (currentFacetContainer) {
      currentFacetContainer.style.display = '';
    }
    if (facetDrawerWrapper) {
      facetDrawerWrapper.style.display = '';
    }

    // Check if we're switching between different filter types (product facets vs blog tag filters)
    // by comparing the data-index values in response vs current DOM
    const currentFilters = document.querySelectorAll('#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]');
    const currentIndexes = Array.from(currentFilters).map(f => f.dataset.index).sort().join(',');
    const newIndexes = Array.from(facetElements).map(f => f.dataset.index).sort().join(',');

    // If indexes don't match, we're switching filter types - replace entire containers
    if (currentIndexes !== newIndexes) {

      // Replace vertical facet container
      const newVerticalContainer = parsedHTML.getElementById('FacetFiltersContainer');
      if (newVerticalContainer && currentFacetContainer) {
        currentFacetContainer.innerHTML = newVerticalContainer.innerHTML;
      }

      // Replace mobile facet drawer container
      const currentMobileContainer = document.getElementById('MobileFacetFiltersContainer');
      const newMobileContainer = parsedHTML.getElementById('MobileFacetFiltersContainer');
      if (newMobileContainer && currentMobileContainer) {
        currentMobileContainer.innerHTML = newMobileContainer.innerHTML;
      }

      // Re-initialize blog tag filter JavaScript if we switched to articles
      if (searchType === 'article' && typeof BlogTagFilter !== 'undefined') {

        new BlogTagFilter();
      }

      return; // Exit early - we replaced everything
    }

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    const layoutSwitcher = document.querySelector('#ProductGridWrapper layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    // Update products loaded count
    const productsLoadedId = 'ProductsLoaded';
    const productsLoadedEl = document.getElementById(productsLoadedId);
    const parsedProductsLoaded = parsedHTML.getElementById(productsLoadedId);
    if (productsLoadedEl && parsedProductsLoaded) {
      productsLoadedEl.innerHTML = parsedProductsLoaded.innerHTML;
    }
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSearchTypeTabs(responseText) {
    const id = 'SearchTypeTabs';
    const container = document.getElementById(id);
    if (container === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const source = parsedHTML.getElementById(id);
    if (source) {
      // Get current type from URL
      const url = new URL(window.location.href);
      const currentType = url.searchParams.get('type');

      // Get the current count from the response immediately for fast update
      const totalEl = parsedHTML.getElementById('TopProductsTotal');
      const currentCount = totalEl ? parseInt(totalEl.textContent.replace(/\D/g, '')) : undefined;

      // Preserve previous counts to avoid flicker to per-page values
      const previousCounts = {
        product: container.querySelector('.type-count[data-type="product"]')?.textContent,
        article: container.querySelector('.type-count[data-type="article"]')?.textContent,
        page: container.querySelector('.type-count[data-type="page"]')?.textContent,
      };

      container.innerHTML = source.innerHTML;

      const setCountIfExists = (type, value) => {
        if (value == null) return;
        const el = container.querySelector(`.type-count[data-type="${type}"]`);
        const countWrap = el?.closest('.count-wrap');
        if (el) {
          el.textContent = String(value);
          // Show the count-wrap (parentheses) now that we have a number
          if (countWrap) {
            countWrap.style.display = '';
          }
        }
      };

      // Set current tab count immediately from response (FAST)
      if (currentType && typeof currentCount === 'number') {
        setCountIfExists(currentType, currentCount);
      }

      // Restore previous counts for other tabs temporarily
      setCountIfExists('product', previousCounts.product);
      setCountIfExists('article', previousCounts.article);
      setCountIfExists('page', previousCounts.page);

      // Then fetch authoritative totals for OTHER tabs in background (SLOW but accurate)
      updateSearchTypeCounts(container, currentType);
    }

    // Update the "Showing X of Y [type]" text at the top
    this.renderTopProductCount(responseText);
  }

  renderTopProductCount(responseText) {
    const id = 'TopProductCount';
    const container = document.getElementById(id);
    if (container === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const source = parsedHTML.getElementById(id);
    if (source) {
      container.innerHTML = source.innerHTML;
    }
  }

  logQuery(url, responseText, source) {
    try {
      const parsedUrl = new URL(url);
      const params = {};
      parsedUrl.searchParams.forEach((value, key) => {
        params[key] = value;
      });

      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const loadedEl = doc.getElementById('TopProductsLoadedCount');
      const totalEl = doc.getElementById('TopProductsTotal');
      const listCount = doc.querySelectorAll('#ProductsList .f-column.card').length;

      const counts = {
        loaded: loadedEl ? parseInt(loadedEl.textContent.replace(/\D/g, '')) : listCount,
        total: totalEl ? parseInt(totalEl.textContent.replace(/\D/g, '')) : undefined,
      };

    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to log search query', e);
    }
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}

customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
    this.facetUpdateUnsubscriber = undefined;
  }

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
        try {
          const doc = new DOMParser().parseFromString(responseText, 'text/html');
          const loadedEl = doc.getElementById('TopProductsLoadedCount');
          const totalEl = doc.getElementById('TopProductsTotal');
          const counts = {
            loaded: loadedEl ? parseInt(loadedEl.textContent.replace(/\D/g, '')) : undefined,
            total: totalEl ? parseInt(totalEl.textContent.replace(/\D/g, '')) : undefined,
          };
          const params = {};
          const parsedUrl = new URL(url);
          parsedUrl.searchParams.forEach((value, key) => {
            params[key] = value;
          });
          // eslint-disable-next-line no-console
        } catch (_) {}
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const gridList = productGridContainer.querySelector('grid-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.card').forEach((item) => {
        productList.appendChild(item);
      });

      if (gridList && typeof gridList.reload === 'function') {
        gridList.reload();
      }

      // Update products loaded count
      LoadMoreButton.updateProductsLoadedCount();
    }
  }

  static updateProductsLoadedCount() {
    const productList = document.getElementById('ProductsList');
    if (!productList) return;

    const loadedCount = productList.querySelectorAll('.f-column.card').length;

    // Update the count in pagination progress
    const productsLoadedCountEl = document.getElementById('ProductsLoadedCount');
    const paginationProgress = document.getElementById('PaginationProgress');

    if (productsLoadedCountEl && paginationProgress) {
      const totalMatch = paginationProgress.textContent.match(/of (\d+)/);

      if (totalMatch && totalMatch[1]) {
        const total = parseInt(totalMatch[1]);
        const progressPercentage = (loadedCount / total) * 100;

        // Update count
        productsLoadedCountEl.textContent = loadedCount;

        // Update the top product count
        const topProductCount = document.getElementById('TopProductCount');
        const topProductsLoadedCountEl = document.getElementById('TopProductsLoadedCount');
        const topProductsTotalEl = document.getElementById('TopProductsTotal');

        if (topProductCount && topProductsLoadedCountEl && topProductsTotalEl) {
          topProductsLoadedCountEl.textContent = loadedCount;
          topProductsTotalEl.textContent = total;
          topProductCount.style.display = 'block';
        }

        // Update progress bar
        const progressFill = paginationProgress.querySelector('.pagination-progress__fill');
        if (progressFill) {
          progressFill.style.width = `${progressPercentage}%`;
        }
      }
    }

    // Also update the old ProductsLoaded element if it exists (for backward compatibility)
    const productsLoadedEl = document.getElementById('ProductsLoaded');
    if (productsLoadedEl) {
      const totalMatch = productsLoadedEl.textContent.match(/of (\d+)/);
      if (totalMatch && totalMatch[1]) {
        const total = totalMatch[1];
        productsLoadedEl.textContent = `Viewing ${loadedCount} of ${total}`;
      }
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}

customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'sleektheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  onButtonClick(event) {
    const target = event.target ? event.target : event;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);

        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;
    const removedClass = ['f-grid-1-cols', 'f-grid-2-cols'];
    removedClass.forEach((removed) => {
      productGrid.classList.remove(removed);
    });

    let addedClass = '';
    switch (layoutMode) {
      case 'grid-1':
        addedClass = 'f-grid-1-cols';
        break;
      case 'grid-2':
        addedClass = 'f-grid-2-cols';
        break;
    }
    productGrid.classList.add(addedClass);
    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });

    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}

customElements.define('layout-switcher', LayoutSwitcher);

// Initialize product count on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    LoadMoreButton.updateProductsLoadedCount();
    const tabs = document.getElementById('SearchTypeTabs');
    if (tabs) {
      // Hide count-wraps (parentheses) until authoritative totals are fetched
      tabs.querySelectorAll('.count-wrap').forEach((el) => {
        el.style.display = 'none';
      });
      updateSearchTypeCounts(tabs);
    }
  });
} else {
  LoadMoreButton.updateProductsLoadedCount();
  const tabs = document.getElementById('SearchTypeTabs');
  if (tabs) {
    // Hide count-wraps (parentheses) until authoritative totals are fetched
    tabs.querySelectorAll('.count-wrap').forEach((el) => {
      el.style.display = 'none';
    });
    updateSearchTypeCounts(tabs);
  }
}

// Helper: fetch authoritative counts for each type (product/article/page)
// skipType: don't fetch this type since we already have it from current response
function updateSearchTypeCounts(container, skipType = null) {
  try {
    const section = document.querySelector('.collection[data-section-id]');
    const sectionId = section ? section.getAttribute('data-section-id') : undefined;
    const baseUrl = new URL(window.location.href);
    const q = baseUrl.searchParams.get('q') || '';

    const types = ['product', 'article', 'page'];
    for (const type of types) {
      // Skip fetching the current type - we already have it!
      if (type === skipType) continue;

      const url = new URL(window.location.href);
      url.searchParams.set('q', q);
      url.searchParams.set('type', type);
      if (sectionId) url.searchParams.set('section_id', sectionId);

      fetch(url.toString())
        .then((r) => r.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const totalEl = doc.getElementById('TopProductsTotal');
          const count = totalEl ? parseInt(totalEl.textContent.replace(/\D/g, '')) : undefined;
          const target = container.querySelector(`.type-count[data-type="${type}"]`);
          const countWrap = target?.closest('.count-wrap');

          if (target && typeof count === 'number') {
            target.textContent = String(count);
            // Show the count-wrap (parentheses) now that we have a number
            if (countWrap) {
              countWrap.style.display = '';
            }
          }
        })
        .catch(() => {});
    }
  } catch (_) {}
}
